<template>
	<div class="s-w-full s-bg-white">
		<JFlex
			v-show="!files.length"
			class="s-flex-col s-w-full s-relative s-h-40 s-border-2 s-border-dashed s-border-indigo-400 s-appearance-none s-flex s-justify-center s-items-center s-mt-2 s-text-gray-900"
		>
			<JFlex class="s-flex-col s-items-center">
				<div class="s-text-xl s-font-semibold">{{ $t("core.dragFilesHereOr") }}</div>
				<JButton variant="default-light" class="s-pointer-event-none s-mt-5 s-mx-auto">
					{{ $t("core.selectFiles") }}
				</JButton>
			</JFlex>
			<input
				id="input_file"
				name="fileUpload"
				type="file"
				multiple="multiple"
				accept=".jpg,.jpeg,.png,.gif,.tiff,.webp,.pdf,.doc,.xdoc,.csv,.xls,.xlsx,.zip,.rar,.txt,.json,.js,.dat"
				class="s-cursor-pointer s-absolute s-inset-0 s-w-full s-h-full s-opacity-0"
				@input="AddFilesUpload($event, 'frontId')"
			/>
		</JFlex>
		<div v-if="files.length">
			<table class="s-w-full">
				<thead class="s-border-b s-border-gray-300">
					<tr class="s-font-semibold">
						<td class="s-pr-4 s-pb-4">
							{{ $t("core.file") }}
						</td>
						<td class="s-pr-4 s-pb-4">
							{{ $t("core.size") }}
						</td>
						<td class="s-pr-4 s-pb-4">
							{{ $t("core.status") }}
						</td>
						<td class="s-pr-4 s-pb-4">
							{{ $t("core.privacyLevel") }}
						</td>
						<td class="s-pb-4 s-text-center">
							{{ $t("core.remove") }}
						</td>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(file, fi) in files" :key="fi">
						<td class="s-pr-4 s-py-2">{{ file.name }}</td>
						<td class="s-pr-4 s-py-2">{{ formatBytes(file.size) }}</td>
						<td
							class="s-pr-4 s-py-2 s-font-medium"
							:class="[
								{ 'text-cSuccess': file.status === 'success' },
								{
									'text-cError': file.status !== 'success' && file.status !== 'uploading'
								}
							]"
						>
							{{ $t(file.status) }}
						</td>
						<td class="s-pr-4 s-py-2">
							<JSelect v-model="file.isPublic" :options="privacyOptions" />
						</td>
						<td class="s-py-2 s-text-center">
							<button class="appearance-none focus:outline-none" @click="removeFile(file)">
								<svg width="18" height="18" viewBox="0 0 24 24" class="s-text-gray-900">
									<path
										fill="currentColor"
										d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
									></path>
									<path data-v-427fdba3="" d="M0 0h24v24H0z" fill="none"></path>
								</svg>
							</button>
						</td>
					</tr>
				</tbody>
			</table>
			<label
				for="input_file"
				class="s-block cursor-pointer s-mt-6 s-text-lg s-font-medium s-text-indigo-500 s-border-b s-border-gray-300"
			>
				+ {{ $t("core.selectMoreFiles") }}
			</label>
			<JFlex class="s-justify-between s-mt-6">
				<JButton variant="white" @click="$emit('cancel')">{{ $t("core.cancel") }}</JButton>
				<JButton variant="default-light" @click="handleUploadFiles"> {{ $t("core.upload") }}</JButton>
			</JFlex>
		</div>
	</div>
</template>

<script>
import axios from "axios"

const getMimeTypeBaseOnExt = (file) => {
	const regex = new RegExp("[^.]+$")
	let extension = file.name.match(regex)
	extension = extension?.[0]?.toString()?.toLowerCase()
	switch (extension) {
		case "csv":
			return "text/csv"
		case "dat":
			return "application/octet-stream"
		case "docx":
			return "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
		case "doc":
			return "application/msword"
		case "xls":
			return "application/vnd.ms-excel"
		case "xlsx":
			return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
		case "msg":
			return "application/vnd.ms-outlook"
		case "pdf":
			return "application/pdf"
		case "jsonata":
			return "application/jsonata"
		case "jsonl":
			return "application/x-ndjson"
		case "yml":
			return "application/x-yaml"
		default:
			return file.type
	}
}

export default {
	props: {
		prefix: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			files: [],
			maxSize: 15 * 1024 * 1024
		}
	},
	computed: {
		privacyOptions() {
			return [
				{ name: "Private", value: false },
				{ name: "Public", value: true }
			]
		}
	},
	methods: {
		async AddFilesUpload(evt, type) {
			this.isLoading = true
			try {
				await this.resolveFileUpload(evt.target.files, type)
			} catch (errors) {
				console.log(errors)
			}
		},
		resolveFileUpload(files, type) {
			return new Promise((resolve, reject) => {
				for (const i in files) {
					if (files[i] instanceof File) {
						const reader = new FileReader()
						reader.readAsDataURL(files[i])
						reader.onload = e => {
							this[type] = e.target.result
							files[i].isPublic = false
							files[i].status = ""
							this.files.push(files[i])
						}
						reader.onerror = e => {
							reject(this.$t("core.fileTypeError"))
							console.log(e)
						}
					}
				}
			})
		},
		removeFile(file) {
			const index = this.files.indexOf(file)
			if (index > -1) {
				this.files.splice(index, 1)
			}
		},
		handleUploadFiles() {
			this.files.forEach(async file => {
				if (file.status === "success") return
				if (file.size > this.maxSize) {
					file.status = `${this.$t("core.fileSizeMustBeUnder")} ${this.formatBytes(this.maxSize)}`
					this.$forceUpdate()
					return
				}
				file.status = "uploading"
				this.$forceUpdate()
				try {
					const formData = new FormData()
					formData.append(
						"file",
						new Blob([file], {
							type: getMimeTypeBaseOnExt(file),
						}),
						file.name
					)
					formData.append("isPublic", file.isPublic)
					const uri = encodeURIComponent(this.prefix)
					const res = await axios.post(`${window.envConfig?.COVERGO_FILE_SYSTEM}/${uri}`, formData, {
						headers: {
							Authorization: `Bearer ${this.$store?.state?.auth?.token}`,
							"Content-Type": "multipart/form-data"
						}
					})
					if (res.status === 200) {
						//eslint-disable-next-line
						file.status = "success"
						this.$forceUpdate()
						this.$nextTick(function() {
							const success = this.files.filter(file => {
								return file.status === "success"
							})
							if (this.files.length && success.length === this.files.length) {
								this.files = []
								this.$emit("file:uploaded")
							}
						})
					} else {
						//eslint-disable-next-line
						file.status = "failure"
						this.$forceUpdate()
					}
				} catch (errors) {
					this.handleErrors(errors, file)
					this.$forceUpdate()
				}
			})
		},

		formatBytes(bytes) {
			if (bytes === 0) return "0 Bytes"
			const k = 1024
			const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
			const i = Math.floor(Math.log(bytes) / Math.log(k))
			return parseFloat((bytes / k ** i).toFixed(2)) + " " + sizes[i]
		},

		handleErrors(res, file) {
			if (res.status === 401) {
				// If 401 - show message that the user is not authorized to do this action
				file.status = "failure"
				this.$store.dispatch(
					"addMessage",
					{
						message: {
							type: "error",
							text: this.$t("core.authorization_error")
						}
					},
					{ root: true }
				)
			} else if (res.status > 401) {
				file.status = "failure"
				// If different error, show general error
				this.$store.dispatch(
					"addMessage",
					{
						message: {
							type: "error",
							text: this.$t("core.generalError")
						}
					},
					{ root: true }
				)
			} else if (res.data && res.data.errors && res.data.errors.includes("Specified Object Already Exists in the Bucket")) {
				file.status = "already_exists"
			} else {
				file.status = typeof res.data === "string" ? res.data : "failure"
			}
		}
	}
}
</script>
